import React from 'react';
import styled from 'styled-components';
import { Product } from '@packages/common';
import { HeightProps, height, size, SizeProps } from 'styled-system';

import { Flex, themeGet, media, isNot, HAS_PSUDO_SKELETON_DARK, HAS_PSUDO_SKELETON_LIGHT } from '@packages/ui';

import { DefaultProductPrice } from '../DefaultProductPrice';
import { ProductDescription } from '../ProductDescription';
import { ShippingAndLocalInfo, LocallyFiltered } from '../ShippingAndLocalInfo';

import { ProductRating } from '../ProductRating';

export const VisibleOnlyOnDesktop = styled.div`
  ${media.to.md`
    display: none;
  `};
`;

interface ProductTileBottomProps {
  product: Product;
  isLocallyFiltered: boolean;
  bottomHeight: string[];
  showPartnerName?: boolean;
  hideShippingInfo: boolean;
}

const BottomWrapper = styled(Flex)<HeightProps & SizeProps>`
  ${size};
  ${height};
`;

const Price = styled(DefaultProductPrice)``;

const Shipping = styled.div<{ offline: boolean }>`
  height: ${themeGet.fontSize(8)};
  ${isNot('offline')`
    ${media.to.md`
      display: none;
    `};
  `}
`;

const RatingsWrapper = styled(Flex)<{ offline: boolean }>`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
  top: 2px;
  ${isNot('offline')`
  ${media.to.md`
    display: none;
  `};
`};
`;

export const ProductTileBottom: React.FC<ProductTileBottomProps> = ({
  product,
  isLocallyFiltered,
  bottomHeight,
  hideShippingInfo,
}) => {
  const { features, price, isOffline = false, shipping, rating } = product;

  const canShowRating = !!(rating && rating.score);
  const freeShipping = shipping && shipping.price === 0;

  const shouldShowShipping = !hideShippingInfo ? !canShowRating || freeShipping : false;
  const shouldShowRatings = canShowRating && !shouldShowShipping;

  return (
    <BottomWrapper
      p={2}
      pt={[2, 2, 3]}
      pb={2}
      height={bottomHeight as any}
      flexDirection="column"
      justifyContent="space-between"
    >
      <Price className={HAS_PSUDO_SKELETON_DARK} features={features} price={price} />
      {isOffline ? (
        <VisibleOnlyOnDesktop>
          <ProductDescription title={product.title} />
        </VisibleOnlyOnDesktop>
      ) : (
        <>
          <ProductDescription title={product.title} />
        </>
      )}

      <>
        {shouldShowShipping && (
          <Shipping offline={isOffline}>
            {!hideShippingInfo && (
              <ShippingAndLocalInfo
                {...product}
                isLocallyFiltered={isLocallyFiltered}
                hideOnMobile={true}
                isOffline={isOffline}
              />
            )}
          </Shipping>
        )}

        {shouldShowRatings && (
          <RatingsWrapper offline={isOffline} className={HAS_PSUDO_SKELETON_LIGHT}>
            <ProductRating rating={rating} />
            {isLocallyFiltered && <LocallyFiltered />}
          </RatingsWrapper>
        )}
      </>
    </BottomWrapper>
  );
};

ProductTileBottom.displayName = 'ProductTileBottom';
